// Cutout Studio: enqueue a background removal on our own GPU, poll for it, then
// hand the result to the shared brush editor for manual refinement.
//
// The upstream worker caches by source URL, so a repeat of the same image comes
// back as "cached" without another model run.

(function init() {
  if (typeof jQuery === 'undefined' || !document.getElementById('cutout-run')) {
    setTimeout(init, 100);
    return;
  }

  const POLL_INTERVAL_MS = 700;
  const POLL_ATTEMPTS = 90; // ~60s, then we stop asking

  const $status = jQuery('#cutout-status');
  const $stage = jQuery('#cutout-stage');
  const $run = jQuery('#cutout-run');
  const $brush = jQuery('#cutout-brush');
  const $download = jQuery('#cutout-download');
  const $urlInput = jQuery('#cutout-url');
  const $sourceImg = jQuery('#cutout-source-img');
  const $placeholder = jQuery('#cutout-placeholder');
  const fileInput = document.getElementById('cutout-file');
  const SAMPLE_URL =
    'https://netwrckstatic.netwrck.com/static/uploads/ai/tools/cutout-studio-sample.webp';

  const state = {
    sourceUrl: '',
    sourceDataUrl: '',
    sourceMime: '',
    sourceFilename: '',
    cutoutUrl: '',
    objectUrl: '',
  };

  function setStatus(text, badge) {
    if (!text) {
      $status.empty();
      return;
    }
    const mark = badge ? ` <span class="cutout-badge ${badge}">${badge}</span>` : '';
    $status.html(text + mark);
  }

  function userSecret() {
    return (window.userData && window.userData.secret) || localStorage.getItem('userSecret') || '';
  }

  function refreshRunButton() {
    $run.prop('disabled', !(state.sourceUrl || state.sourceDataUrl));
  }

  function canvasSafeUrl(src) {
    if (!src || /^(data:|blob:)/i.test(src)) return src;
    try {
      const parsed = new URL(src, window.location.href);
      if (parsed.origin === window.location.origin) return parsed.href;
      return '/api/img-proxy?url=' + encodeURIComponent(parsed.href);
    } catch (_) {
      return src;
    }
  }

  function releaseObjectUrl() {
    if (!state.objectUrl) return;
    URL.revokeObjectURL(state.objectUrl);
    state.objectUrl = '';
  }

  function showSource(src) {
    $sourceImg.attr('src', src).show();
    $placeholder.hide();
    jQuery('#cutout-drop').addClass('has-image');
  }

  jQuery('#cutout-drop').on('click', function () {
    fileInput.click();
  });

  jQuery('#cutout-drop').on('dragover', function (event) {
    event.preventDefault();
  });

  jQuery('#cutout-drop').on('drop', function (event) {
    event.preventDefault();
    const file = event.originalEvent.dataTransfer.files[0];
    if (file) readFile(file);
  });

  jQuery(fileInput).on('change', function () {
    if (this.files && this.files[0]) readFile(this.files[0]);
  });

  $urlInput.on('input', function () {
    state.sourceUrl = jQuery(this).val().trim();
    state.sourceDataUrl = '';
    state.sourceMime = '';
    state.sourceFilename = '';
    if (state.sourceUrl) showSource(state.sourceUrl);
    refreshRunButton();
  });

  jQuery('#cutout-sample').on('click', function (event) {
    event.preventDefault();
    event.stopPropagation();
    $urlInput.val(SAMPLE_URL);
    state.sourceUrl = SAMPLE_URL;
    state.sourceDataUrl = '';
    state.sourceMime = '';
    state.sourceFilename = '';
    showSource(SAMPLE_URL);
    refreshRunButton();
  });

  function readFile(file) {
    const reader = new FileReader();
    reader.onload = function (event) {
      state.sourceDataUrl = event.target.result;
      state.sourceUrl = '';
      state.sourceMime = file.type || 'application/octet-stream';
      state.sourceFilename = file.name || 'cutout-source';
      $urlInput.val('');
      showSource(state.sourceDataUrl);
      refreshRunButton();
    };
    reader.readAsDataURL(file);
  }

  // The worker fetches by URL, so a local file has to be uploaded first.
  function ensureSourceUrl() {
    if (state.sourceUrl) return Promise.resolve(state.sourceUrl);
    if (!state.sourceDataUrl) return Promise.reject(new Error('Choose an image first'));

    setStatus('Uploading image...');
    return fetch('/api/image-editor/upload', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + userSecret() },
      body: JSON.stringify({
        bytes: state.sourceDataUrl,
        content_type: state.sourceMime,
        filename: state.sourceFilename,
      }),
    })
      .then((response) =>
        response.json().then((upload) => ({ ok: response.ok, upload })),
      )
      .then(({ ok, upload }) => {
        if (!ok || !upload || !upload.url) throw new Error((upload && upload.error) || 'Upload failed');
        state.sourceUrl = upload.url;
        return upload.url;
      });
  }

  function poll(jobId, attempt) {
    return fetch('/api/cutout/status?job=' + encodeURIComponent(jobId), {
      headers: { Authorization: 'Bearer ' + userSecret() },
    })
      .then((response) =>
        response.json().then((job) => ({ ok: response.ok, job })),
      )
      .then(({ ok, job }) => {
        if (!ok) throw new Error((job && job.error) || 'Could not read cutout status');
        if (job.status === 'done') return job;
        if (job.status === 'error') throw new Error(job.error || 'Cutout failed');
        if (attempt >= POLL_ATTEMPTS) throw new Error('Timed out waiting for the cutout');
        setStatus('Working... (' + Math.round((attempt * POLL_INTERVAL_MS) / 1000) + 's)');
        return new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS)).then(() =>
          poll(jobId, attempt + 1),
        );
      });
  }

  function cutoutResultURL(payload) {
    if (!payload) return '';
    return (
      payload.url ||
      payload.data_url ||
      payload.image_url ||
      (payload.image && payload.image.url) ||
      (payload.result && payload.result.image && payload.result.image.url) ||
      (payload.result && payload.result.images && payload.result.images[0] && payload.result.images[0].url) ||
      ''
    );
  }

  function showResult(job) {
    const src = cutoutResultURL(job);
    if (!src) throw new Error('Cutout produced no image');
    releaseObjectUrl();
    state.cutoutUrl = src;

    $stage.empty();
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = src;
    $stage.append(image);

    $download.attr('href', canvasSafeUrl(src)).attr('download', 'cutout.webp').show();
    $brush.prop('disabled', false);
    setStatus(job.cached ? 'BiRefNet cutout loaded from cache.' : 'BiRefNet cutout ready.', job.cached ? 'cached' : 'fresh');
  }

  $run.on('click', function () {
    if (window.NetwrckPaywall) {
      if (!window.NetwrckPaywall.require({ feature: 'Cutout Studio', credits: 2 })) return;
    } else if (window.NetwrckTools && !window.NetwrckTools.requireLogin()) return;

    $run.prop('disabled', true);
    $brush.prop('disabled', true);
    $download.hide();
    setStatus('Starting BiRefNet...');

    ensureSourceUrl()
      .then((imageUrl) =>
        fetch('/api/cutout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + userSecret() },
          body: JSON.stringify({
            image_url: imageUrl,
            decontaminate: jQuery('#cutout-decontaminate').is(':checked'),
            cache: jQuery('#cutout-cache').is(':checked'),
          }),
        }).then((response) =>
          response.json().catch(() => ({})).then((job) => {
            if (window.NetwrckPaywall && window.NetwrckPaywall.check(response, job, { feature: 'Cutout Studio' })) {
              throw window.NetwrckPaywall.error();
            }
            return { ok: response.ok, status: response.status, job };
          }),
        ),
      )
      .then(({ ok, status, job }) => {
        if (ok && job && !job.error && job.status === 'done') return job;
        if (ok && job && job.job_id) return poll(job.job_id, 1);
        throw new Error((job && job.error) || 'Background removal failed');
      })
      .then(showResult)
      .catch((error) => {
        setStatus('');
        if (error && error.paywall) return;
        if (window.NetwrckTools && window.NetwrckTools.showNotification) {
          window.NetwrckTools.showNotification(error.message || 'Cutout failed', 'error');
        } else {
          alert(error.message || 'Cutout failed');
        }
      })
      .then(() => {
        $run.prop('disabled', false);
      });
  });

  $brush.on('click', function () {
    if (!state.cutoutUrl || !window.MaskBrushEditor) return;
    window.MaskBrushEditor.open({
      imageUrl: canvasSafeUrl(state.cutoutUrl),
      originalUrl: canvasSafeUrl(state.sourceUrl || state.sourceDataUrl),
      title: 'Refine Cutout',
      applyLabel: 'Apply Cutout',
      onApply: function (blob) {
        if (!blob) {
          setStatus('Brush editor could not export the image.');
          return;
        }
        releaseObjectUrl();
        const objectUrl = URL.createObjectURL(blob);
        state.objectUrl = objectUrl;
        state.cutoutUrl = objectUrl;
        $stage.empty();
        const image = new Image();
        image.src = objectUrl;
        $stage.append(image);
        $download.attr('href', objectUrl).attr('download', 'cutout.png').show();
        setStatus('Refinement applied.');
      },
      onError: function () {
        setStatus('Brush editor failed to load the image.');
      },
    });
  });

  refreshRunButton();
  window.addEventListener('beforeunload', releaseObjectUrl);
  console.log('[tools] cutout-studio ready');
})();
